import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { BookingButtonManager } from '@theme/booking-state';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    
    // Populate customer data for government products
    this.#populateCustomerData();
  }

  /**
   * Populate customer data from localStorage for government products
   */
  #populateCustomerData() {
    try {
      const customerDataStr = localStorage.getItem('customerData');
      if (!customerDataStr) return;

      const customerData = JSON.parse(customerDataStr);
      
      // Find all customer info containers
      const customerInfoContainers = this.querySelectorAll('[data-customer-info]');
      customerInfoContainers.forEach(container => {
        const nameEl = container.querySelector('[data-customer-name]');
        const appNumberEl = container.querySelector('[data-customer-app-number]');
        const appNumberRow = appNumberEl?.closest('p');
        
        if (nameEl && customerData.applicantName) {
          nameEl.textContent = customerData.applicantName;
        }
        if (appNumberEl && customerData.applicationNumber) {
          appNumberEl.textContent = customerData.applicationNumber;
        } else if (appNumberRow) {
          appNumberRow.style.display = 'none';
        }
      });
    } catch (e) {
      console.error('Error populating customer data:', e);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    
    // Check if this is a government product being removed
    const productType = cartItemRowToRemove?.dataset?.productType || '';
    const isGovtProduct = productType === 'government' || cartItemRowToRemove?.classList.contains('government-product');
    
    // If a government product is removed, remove both items simultaneously
    if (isGovtProduct) {
      BookingButtonManager.enableAll();
      this.#removeGovtProductAndServiceFee(line);
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }
  
  /**
   * Remove government product and service fee simultaneously using cart/update.js
   * @param {number} govtLine - The line number of the government product
   */
  async #removeGovtProductAndServiceFee(govtLine) {
    try {
      // Fetch current cart to find the service fee item
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Build updates object - set both items to 0 quantity
      const updates = {};
      
      // Set the government product quantity to 0
      const govtItem = cart.items[govtLine - 1];
      if (govtItem) {
        updates[govtItem.key] = 0;
      }
      
      // Find and add service fee to updates
      const serviceFeeItem = cart.items.find(item => item.handle === 'salem-service-fee');
      if (serviceFeeItem) {
        updates[serviceFeeItem.key] = 0;
      }
      
      // Hide service fee display immediately
      const serviceFeeDisplay = document.querySelector('.cart__service-fee');
      if (serviceFeeDisplay) {
        serviceFeeDisplay.style.display = 'none';
      }
      
      // Get section ID for proper refresh
      const sectionsToUpdate = new Set([this.sectionId]);
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });
      
      // Use cart/update.js to remove both items at once
      const removeResponse = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname
        })
      });
      
      const result = await removeResponse.json();
      
      // Get new cart item count
      const newSectionHTML = new DOMParser().parseFromString(
        result.sections[this.sectionId],
        'text/html'
      );
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
      
      // Dispatch cart update event with sections data
      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'govt-service-removal',
          sections: result.sections,
        })
      );
      
      // Morph the cart to update UI
      this.#selectiveMorphCart(result.sections[this.sectionId]);
      
      console.log('Government product and service fee removed simultaneously');
    } catch (e) {
      console.warn('Error removing govt product and service fee:', e);
      // Fallback to regular removal
      this.updateQuantity({
        line: govtLine,
        quantity: 0,
        action: 'clear',
      });
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // Use selective morph to preserve booking data (progress-steps, booking-summary)
        this.#selectiveMorphCart(parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#selectiveMorphCart(cartItemsHtml);
    } else {
      this.#fetchAndSelectiveMorph();
    }
  };

  /**
   * Fetches fresh section HTML and selectively morphs cart parts.
   */
  async #fetchAndSelectiveMorph() {
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
      this.#selectiveMorphCart(html);
    } catch (e) {
      console.error('Error fetching section HTML:', e);
    }
  }

  /**
   * Selectively morphs only specific cart parts to preserve booking data.
   * Updates: header, items, summary
   * Preserves: progress-steps, booking-summary
   * @param {string} html - The new section HTML
   */
  #selectiveMorphCart(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newCartItems = doc.querySelector('cart-items-component');
    
    if (!newCartItems) return;

    // Check if transitioning from empty to non-empty cart
    const currentContent = this.querySelector('.cart-drawer__content');
    const newContent = newCartItems.querySelector('.cart-drawer__content');
    const wasEmpty = currentContent?.querySelector('.cart-drawer__empty-state');
    const isNowEmpty = newContent?.querySelector('.cart-drawer__empty-state');

    // Get the dialog element to toggle cart-drawer--empty class
    const dialog = this.closest('dialog') || document.querySelector('.cart-drawer__dialog');

    // 1. Always update cart-drawer__header (cart count)
    const currentHeader = this.querySelector('.cart-drawer__header');
    const newHeader = newCartItems.querySelector('.cart-drawer__header');
    if (currentHeader && newHeader) {
      currentHeader.innerHTML = newHeader.innerHTML;
    }

    if (wasEmpty && !isNowEmpty) {
      // Cart was empty, now has items - replace entire content div
      if (currentContent && newContent) {
        currentContent.outerHTML = newContent.outerHTML;
      }
      // Remove cart-drawer--empty class from dialog
      dialog?.classList.remove('cart-drawer--empty');
    } else if (!wasEmpty && !isNowEmpty) {
      // Cart already had items, selectively update parts
      // 2. Update cart-drawer__items (cart products)
      const currentItems = this.querySelector('.cart-drawer__items');
      const newItems = newCartItems.querySelector('.cart-drawer__items');
      if (currentItems && newItems) {
        currentItems.innerHTML = newItems.innerHTML;
      }

      // 3. Update cart-drawer__summary (totals and buttons)
      const currentSummary = this.querySelector('.cart-drawer__summary');
      const newSummary = newCartItems.querySelector('.cart-drawer__summary');
      if (currentSummary && newSummary) {
        currentSummary.innerHTML = newSummary.innerHTML;
      }
    } else if (!wasEmpty && isNowEmpty) {
      // Cart had items, now empty - replace entire content div
      if (currentContent && newContent) {
        currentContent.outerHTML = newContent.outerHTML;
      }
      // Add cart-drawer--empty class to dialog
      dialog?.classList.add('cart-drawer--empty');
    }

    // Re-populate customer data after DOM update
    this.#populateCustomerData();
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
